// `useToast()` Preact hook. Returns the current toast queue + the
// imperative `addToast` / `removeToast` mutators. Thin wrapper over
// `useSubscribed` (P-035 hoist) — same shape as `useStore` /
// `usePersistenceError` / `useLastSavedAt` / `usePendingWrites`.
//
// **Why pass-through mutators (not local-rebound).** `addToast` /
// `removeToast` are pure module-scope functions; binding them inside
// the hook would re-create the references on every render and break
// downstream `React.memo` / `useCallback` equality. Pass through.

import { useSubscribed } from '../../lib/use-subscribed'
import type { Toast, ToastAction, ToastKind } from './toast-store'
import {
  addToast,
  getToasts,
  removeToast,
  subscribeToasts,
} from './toast-store'

export interface UseToast {
  readonly toasts: readonly Toast[]
  readonly addToast: (input: {
    kind: ToastKind
    message: string
    action?: ToastAction
  }) => string
  readonly removeToast: (id: string) => void
}

export function useToast(): UseToast {
  const toasts = useSubscribed(getToasts, subscribeToasts)
  return { toasts, addToast, removeToast }
}
